import axios from 'axios';
import { server } from "../config";
import { getCookie } from "../container/LoginHelper";

const BASE_URL = server;

/**
 * 帶上登入 token（品牌相關操作後端會檢查網管權限）
 */
const authHeaders = () => {
  const token = getCookie();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * 處理 API 響應，檢查 code 並返回 data 或拋出錯誤
 * @param {Object} response - axios 響應物件
 * @returns {Object} 響應的 data 部分
 * @throws {Error} 如果 code 不為 200
 */
const handleResponse = (response) => {
  if (response.data.code === 200) {
    return response.data.data;
  } else {
    throw new Error(response.data.message || 'API 錯誤');
  }
};

/**
 * 處理錯誤，根據錯誤類型拋出適當的錯誤訊息
 * @param {Error} error - axios 錯誤物件
 * @throws {Error} 處理後的錯誤
 */
const handleError = (error) => {
  if (error.response) {
    // 伺服器回應錯誤狀態
    throw new Error(error.response.data.message || `HTTP ${error.response.status}: ${error.response.statusText}`);
  } else if (error.request) {
    // 網路錯誤
    throw new Error('網路錯誤：無法連接到伺服器');
  } else {
    // 其他錯誤
    throw new Error(error.message || '未知錯誤');
  }
};

/**
 * 取得所有公司
 * @returns {Promise<Array>} 公司列表
 */
export const getAllCompanies = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/companies`);
    return handleResponse(response);
  } catch (error) {
    handleError(error);
  }
};

/**
 * 根據 ID 取得單一公司
 * @param {string|number} id - 公司 ID
 * @returns {Promise<Object>} 公司物件
 */
export const getCompanyById = async (id) => {
  try {
    const response = await axios.get(`${BASE_URL}/companies/${id}`);
    return handleResponse(response);
  } catch (error) {
    handleError(error);
  }
};

/**
 * 創建新公司
 * @param {Object} companyData - 公司資料
 * @param {string} companyData.company_name - 公司名稱 (必填)
 * @param {string} companyData.theme_name - 主題名稱 (必填)
 * @param {boolean} companyData.is_active - 是否啟用 (必填)
 * @param {string} [companyData.login_slug] - 專屬登入網址代碼 (選填，需網管權限)
 * @param {string} [companyData.site_title] - 網站標題 (選填，需網管權限)
 * @returns {Promise<Object>} 創建的公司物件
 */
export const createCompany = async (companyData) => {
  const response = await axios.post(`${BASE_URL}/companies`, companyData, { headers: authHeaders() });
  return handleResponse(response);
};

/**
 * 更新公司
 * @param {string|number} id - 公司 ID
 * @param {Object} companyData - 要更新的公司資料
 * @returns {Promise<Object>} 更新後的公司物件
 */
export const updateCompany = async (id, companyData) => {
  try {
    const response = await axios.put(`${BASE_URL}/companies/${id}`, companyData, {
      headers: authHeaders(),
    });
    return handleResponse(response);
  } catch (error) {
    handleError(error);
  }
};

/**
 * 刪除公司
 * @param {string|number} id - 公司 ID
 * @returns {Promise<Object>} 刪除結果
 */
export const deleteCompany = async (id) => {
  try {
    const response = await axios.delete(`${BASE_URL}/companies/${id}`, { headers: authHeaders() });
    return handleResponse(response);
  } catch (error) {
    handleError(error);
  }
};

/**
 * 依登入網址代碼取得公司品牌（公開 API，登入頁使用）
 * @param {string} slug
 * @returns {Promise<Object|null>} 查無資料時回傳 null
 */
export const getBrandingBySlug = async (slug) => {
  const response = await axios.get(`${BASE_URL}/branding/slug/${encodeURIComponent(slug)}`);
  if (response.data.code !== 200) return null;
  return response.data.data || null;
};

/**
 * 上傳品牌圖片（需網管權限）
 * @param {string|number} id - 公司 ID
 * @param {'logo'|'login_image'|'favicon'} kind - 圖片類型
 * @param {File} file - 圖片檔案
 * @returns {Promise<Object>} 更新後的公司物件
 */
export const uploadBrandingImage = async (id, kind, file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(`${BASE_URL}/branding/upload/${id}/${kind}`, formData, {
      headers: authHeaders(), // Content-Type 交給瀏覽器帶 boundary
    });
    return handleResponse(response);
  } catch (error) {
    handleError(error);
  }
};

/**
 * 移除品牌圖片，恢復系統預設（需網管權限）
 * @param {string|number} id - 公司 ID
 * @param {'logo'|'login_image'|'favicon'} kind - 圖片類型
 * @returns {Promise<Object>} 更新後的公司物件
 */
export const deleteBrandingImage = async (id, kind) => {
  try {
    const response = await axios.delete(`${BASE_URL}/branding/upload/${id}/${kind}`, {
      headers: authHeaders(),
    });
    return handleResponse(response);
  } catch (error) {
    handleError(error);
  }
};

export default {
  getAllCompanies,
  getCompanyById,
  createCompany,
  updateCompany,
  deleteCompany,
  getBrandingBySlug,
  uploadBrandingImage,
  deleteBrandingImage,
};